import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ENV } from "../config/env";
import { requestNotifyPermission, showBrowserNotification } from "../lib/swNotify";

const FALLBACK_POLL_INTERVAL = 5 * 60 * 1000; // 5 min — only used if SSE disconnects
const TOAST_DURATION = 6000;
const PAGE_SIZE = 15;
const DESKTOP_SHOWN_KEY = "notif_desktop_shown_v2";

// ─── localStorage helpers ─────────────────────────────────────────────────────

function getShownIds() {
  try { return new Set(JSON.parse(localStorage.getItem(DESKTOP_SHOWN_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveShownId(id) {
  const ids = getShownIds(); ids.add(id);
  localStorage.setItem(DESKTOP_SHOWN_KEY, JSON.stringify([...ids]));
}

function formatTime(iso) {
  const diffMins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const h = Math.floor(diffMins / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ toast, onClose, onView }) {
  useEffect(() => {
    const t = setTimeout(onClose, TOAST_DURATION);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 w-full sm:w-80 max-w-[calc(100vw-2rem)] animate-slide-in">
      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
        <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 leading-tight">{toast.title}</p>
        {toast.message && (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">{toast.message}</p>
        )}
        {toast.link && (
          <button onClick={() => onView(toast)} className="mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-700">
            View →
          </button>
        )}
      </div>
      <button onClick={onClose} className="flex-shrink-0 text-gray-400 hover:text-gray-600 mt-0.5">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [toasts, setToasts] = useState([]);
  const [notifPermission, setNotifPermission] = useState(
    "Notification" in window ? Notification.permission : "denied"
  );

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const knownIdsRef = useRef(null);
  const permissionAskedRef = useRef(false);
  const esRef = useRef(null); // EventSource ref

  const totalPages = Math.max(1, Math.ceil(notifications.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = notifications.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ── Toast helpers ────────────────────────────────────────────────────────────
  const removeToast = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);

  const handleToastView = useCallback(async (toast) => {
    removeToast(toast.id);
    try {
      await api.delete(`/admin/admin-notifications/${toast.id}`);
      setNotifications((p) => p.filter((n) => n.id !== toast.id));
      setUnreadCount((p) => Math.max(0, p - 1));
    } catch { /* ignore */ }
    if (toast.link) navigate(toast.link);
  }, [removeToast, navigate]);

  // ── Fire OS notification for a single notif (only if not already shown) ─────
  const fireOsNotif = useCallback((notif) => {
    if (Notification.permission !== "granted") return;
    const shown = getShownIds();
    if (shown.has(notif.id)) return;
    showBrowserNotification(notif.title, notif.message || "", notif.id);
    saveShownId(notif.id); // mark AFTER actually firing
  }, []);

  // ── Handle a new arriving notification (SSE or first-load) ──────────────────
  const handleIncomingNotif = useCallback((notif, isFirstLoad = false) => {
    fireOsNotif(notif);
    // In-app toast for new arrivals
    if (isFirstLoad || !(knownIdsRef.current?.has(notif.id))) {
      setToasts((p) => p.some((t) => t.id === notif.id) ? p : [...p, notif]);
    }
  }, [fireOsNotif]);

  // ── Full list fetch (initial load + fallback poll) ───────────────────────────
  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/admin-notifications?limit=200&page=1");
      if (!data.success) return;

      const isFirstLoad = knownIdsRef.current === null;
      const prevIds = knownIdsRef.current ?? new Set();
      knownIdsRef.current = new Set(data.data.map((n) => n.id));

      setNotifications(data.data);
      setUnreadCount(data.unread_count);

      // Trigger desktop + toast for unread items
      for (const notif of data.data.filter((n) => !n.is_read)) {
        const isNew = !prevIds.has(notif.id);
        if (isFirstLoad || isNew) await handleIncomingNotif(notif, isFirstLoad);
      }
    } catch { /* silent */ }
  }, [handleIncomingNotif]);

  // ── SSE connection ────────────────────────────────────────────────────────────
  const connectSSE = useCallback(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) return;

    // Close any existing connection
    if (esRef.current) { esRef.current.close(); esRef.current = null; }

    const url = `${ENV.API_BASE_URL}/admin/admin-notifications/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type !== "new_notification") return;
        const notif = payload.notification;

        // Add to list and update count instantly
        setNotifications((prev) => {
          if (prev.some((n) => n.id === notif.id)) return prev;
          return [notif, ...prev];
        });
        setUnreadCount((p) => p + 1);

        handleIncomingNotif(notif, false);

        // If a customer confirmed an action, broadcast to any open ticket view
        if (notif.type === "CUSTOMER_CONFIRMED" && notif.entity_id) {
          window.dispatchEvent(new CustomEvent("navigatio:ticket-update", {
            detail: { ticket_id: Number(notif.entity_id) },
          }));
        }

        // Mark as known after handling so poll doesn't re-trigger toast/OS notif
        if (knownIdsRef.current) knownIdsRef.current.add(notif.id);
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => {
      // On disconnect, close and reconnect after 10s
      es.close();
      esRef.current = null;
      setTimeout(connectSSE, 10_000);
    };
  }, [handleIncomingNotif]);

  // ── Mount: request permission FIRST, then fetch + SSE ───────────────────────
  useEffect(() => {
    async function init() {
      // Await permission before fetching — so first-load notifications fire correctly
      if (!permissionAskedRef.current) {
        permissionAskedRef.current = true;
        const granted = await requestNotifyPermission();
        setNotifPermission(granted ? "granted" : ("Notification" in window ? Notification.permission : "denied"));
      }
      fetchNotifications();
      connectSSE();
    }
    init();

    const fallbackId = setInterval(fetchNotifications, FALLBACK_POLL_INTERVAL);

    return () => {
      clearInterval(fallbackId);
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
    };
  }, [fetchNotifications, connectSSE]);

  // ── Outside click ────────────────────────────────────────────────────────────
  useEffect(() => {
    function handler(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { if (open) setPage(1); }, [open]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleNotifClick = async (notif) => {
    try {
      await api.delete(`/admin/admin-notifications/${notif.id}`);
      setNotifications((p) => p.filter((n) => n.id !== notif.id));
      if (!notif.is_read) setUnreadCount((p) => Math.max(0, p - 1));
    } catch { /* ignore */ }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const handleClearAll = async () => {
    try {
      await api.delete("/admin/admin-notifications/clear-all");
      setNotifications([]);
      setUnreadCount(0);
      setPage(1);
    } catch { /* ignore */ }
  };

  return (
    <>
      {/* ── Toast stack ──────────────────────────────────────────────── */}
      <div className="fixed top-4 right-4 left-4 sm:left-auto z-[100] flex flex-col items-end gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast toast={toast} onClose={() => removeToast(toast.id)} onView={handleToastView} />
          </div>
        ))}
      </div>

      {/* ── Bell + dropdown ───────────────────────────────────────────── */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setOpen((p) => !p)}
          className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Notifications"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {open && (
          <div className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 top-16 sm:top-auto mt-0 sm:mt-2 w-auto sm:w-96 max-w-full sm:max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-gray-800">Notifications</span>
                {notifications.length > 0 && (
                  <span className="text-xs text-gray-400">({notifications.length})</span>
                )}
              </div>
              {notifications.length > 0 && (
                <button onClick={handleClearAll} className="text-xs text-red-500 hover:text-red-600 font-medium">
                  Clear all
                </button>
              )}
            </div>

            {/* Desktop notification permission banner */}
            {notifPermission !== "granted" && (
              <div className={`px-4 py-2.5 flex items-center justify-between text-xs ${notifPermission === "denied" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-800"}`}>
                <span>
                  {notifPermission === "denied"
                    ? "Desktop notifications blocked — enable in browser site settings"
                    : "Enable desktop notifications for instant alerts"}
                </span>
                {notifPermission !== "denied" && (
                  <button
                    onClick={async () => {
                      const granted = await requestNotifyPermission();
                      setNotifPermission(granted ? "granted" : Notification.permission);
                      if (granted) notifications.filter((n) => !n.is_read).forEach((n) => fireOsNotif(n));
                    }}
                    className="ml-2 flex-shrink-0 font-semibold underline hover:no-underline"
                  >
                    Enable
                  </button>
                )}
              </div>
            )}

            {/* List */}
            <div className="divide-y divide-gray-50 max-h-[60vh] sm:max-h-[420px] overflow-y-auto">
              {pageItems.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications</div>
              ) : (
                pageItems.map((notif) => (
                  <button
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors group ${!notif.is_read ? "bg-blue-50 hover:bg-blue-100" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {!notif.is_read && (
                        <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" />
                      )}
                      <div className={`flex-1 min-w-0 ${!notif.is_read ? "" : "pl-4"}`}>
                        <p className="text-sm font-medium text-gray-800 leading-tight truncate">{notif.title}</p>
                        {notif.message && (
                          <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">{notif.message}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{formatTime(notif.created_at)}</p>
                      </div>
                      <span className="flex-shrink-0 text-xs text-gray-400 group-hover:text-blue-500 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        View →
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2.5 border-t bg-gray-50">
                <button
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Prev
                </button>
                <span className="text-xs text-gray-500">Page {safePage} of {totalPages}</span>
                <button
                  disabled={safePage === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  Next
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
